// ProjectHeader.tsx
import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import {
    WorkOutline,
    Business,
    Event,
    Tag,
    Numbers,
} from "@mui/icons-material";
import { Localized } from "@fluent/react";
import { Project } from "../projectSlice";
import { ProjectStatusPill } from "./ProjectStatusPill";

const dateFmt = new Intl.DateTimeFormat("pl-PL", {
    dateStyle: "medium",
});

export type NullableDate = Date | string | null | undefined;

export function formatDate(value: NullableDate): string {
    if (!value) return "—";
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? "—" : dateFmt.format(d);
}


type ProjectHeaderProps = {
    project: Project;
};

export const ProjectHeader: React.FC<ProjectHeaderProps> = ({ project }) => {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 0,
                mb: 1.5,
                position: "relative",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    bgcolor: (t) =>
                        t.palette.mode === "dark" ? "grey.900" : "grey.100",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    px: 1.25,
                    py: 1.25,
                    borderTopLeftRadius: 1,
                    borderTopRightRadius: 1,
                }}
            >
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    alignItems={{ md: "center" }}
                    justifyContent="space-between"
                    spacing={1.5}
                    sx={{ minWidth: 0 }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        sx={{ minWidth: 0, flex: 1 }}
                    >
                        <Stack sx={{ minWidth: 0 }}>
                            <Box sx={{ height: 20, display: "flex", alignItems: "center", color: "text.secondary" }}>
                                <Tag sx={{ fontSize: 16 }} />
                            </Box>
                            <Box
                                sx={{
                                    mt: 0.25,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    bgcolor: "background.paper",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    whiteSpace: "nowrap",
                                }}
                                title={String(project.id)}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
                                    noWrap
                                >
                                    {project.id}
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: 20, color: "text.secondary" }}>
                                <Numbers sx={{ fontSize: 15 }} />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 600, letterSpacing: ".02em" }}
                                >
                                    <Localized id="project-no">
                                        Numer projektu
                                    </Localized>
                                </Typography>
                            </Stack>
                            <Box
                                sx={{
                                    mt: 0.25,
                                    px: 0.25,
                                    py: 0.25,
                                    pr: 1,
                                    borderRadius: 1,
                                    bgcolor: "background.paper",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    fontWeight: 800,
                                    whiteSpace: "nowrap",
                                    maxWidth: 350,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                                title={project?.no ?? ""}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 800 }}
                                    noWrap
                                >
                                    {project?.no ?? "—"}
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: 20, color: "text.secondary" }}>
                                <WorkOutline sx={{ fontSize: 15 }} />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 600, letterSpacing: ".02em" }}
                                >
                                    <Localized id="project-title">
                                        _Tytuł projektu
                                    </Localized>
                                </Typography>
                            </Stack>
                            <Box
                                sx={{
                                    mt: 0.25,
                                    px: 0.25,
                                    py: 0.25,
                                    pr: 1,
                                    borderRadius: 1,
                                    bgcolor: "background.paper",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    fontWeight: 800,
                                    maxWidth: "100%",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                title={project?.name ?? ""}
                            >
                                <Stack direction="row" spacing={0.5} alignItems="left">
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 800 }}
                                        noWrap
                                    >
                                        {project?.name ?? "—"}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Stack>
                    </Stack>

                    <Stack alignItems={{ xs: "flex-start", md: "flex-start" }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: ".02em" }}>
                            <Localized id="status">Status</Localized>
                        </Typography>
                        <Box sx={{ mt: 0.25 }}>
                            <ProjectStatusPill status={project.status} />
                        </Box>
                    </Stack>
                </Stack>
            </Box>

            <Box sx={{ px: 1.25, py: 1.25 }}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    sx={{ minWidth: 0 }}
                >
                    <Stack spacing={1} flex={1}>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ minWidth: 0 }}
                        >
                            <Business fontSize="small" />
                            <Typography
                                variant="body2"
                                noWrap
                                title={project?.companyName ?? ""}
                            >
                                <strong>
                                    <Localized id="project-company-name">
                                        Nazwa firmy
                                    </Localized>
                                    :
                                </strong>
                                &nbsp;
                                {project?.companyName ?? "—"}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack spacing={1} flex={1}>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                        >
                            <Event fontSize="small" />
                            <Typography variant="body2">
                                <strong>
                                    <Localized id="project-create-date">
                                        Data utworzenia
                                    </Localized>
                                    :
                                </strong>
                                &nbsp;
                                {formatDate(project?.createDate)}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </Box>
        </Paper>
    );
};
